import { useState, useEffect } from "react";
import axios from "axios";
import { getPolicyGrandTotal } from "../utils/policyCalc";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Users, FileText, AlertTriangle, Euro, ArrowRight, Receipt, TrendingUp, CloudOff, Cloud, CheckCircle, RefreshCw, Calendar, Phone, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseISO, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";
import { toast } from "sonner";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns "d MMM").
// NB: formatCurrency resta quello locale piu' sotto, non si importa da lib/format.
import { formatDate } from "../lib/format";
import { Skeleton, CardSkeleton } from "../components/ui/skeleton";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

// Theme-consistent chart colors
const COLORS = {
  primary: "hsl(150, 30%, 24%)",
  secondary: "hsl(27, 50%, 64%)",
  success: "#16a34a",
  warning: "#d97706",
  danger: "#dc2626",
  muted: "#71717a",
  blue: "#2563eb",
};

const PIE_COLORS = ["hsl(150, 30%, 24%)", "hsl(27, 50%, 64%)", "#2563eb", "#d97706", "#dc2626", "#0891b2", "#7c3aed", "#65a30d"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveConnected, setDriveConnected] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if redirected from Drive OAuth
    if (searchParams.get('drive_connected') === 'true') {
      toast.success("MEGA connesso con successo!");
      // Clean URL
      window.history.replaceState({}, '', '/dashboard');
    }
    
    fetchData();
    checkDriveStatus();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [statsRes, expiringRes, chartsRes] = await Promise.all([
        axios.get('/api/dashboard/stats'),
        axios.get('/api/dashboard/expiring?days=30'),
        axios.get('/api/dashboard/charts')
      ]);
      setStats(statsRes.data);
      setExpiring(expiringRes.data);
      setCharts(chartsRes.data);
    } catch (error) {
      // Dashboard load error - stats will show 0
    } finally {
      setLoading(false);
    }
  };

  const checkDriveStatus = async () => {
    try {
      // v3.8: lo storage è MEGA. Controlla lo stato MEGA invece di Drive.
      const response = await axios.get('/api/storage/status');
      setDriveConnected(response.data.mega_connected || response.data.drive_connected);
    } catch (error) {
      // status check failed - show as not connected
    }
  };

  // v3.9: MEGA si configura sul server (.env), non serve un pulsante


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Clienti Totali",
      value: stats?.total_clients || 0,
      icon: Users,
      color: "text-primary",
      href: "/clients"
    },
    {
      title: "Polizze Attive",
      value: stats?.active_policies || 0,
      icon: FileText,
      color: "text-secondary",
      href: "/policies"
    },
    {
      title: "Premi Totali",
      value: formatCurrency(stats?.total_premiums || 0),
      icon: Euro,
      color: "text-primary",
      href: "/reports"
    },
    {
      title: "Consulenze",
      value: formatCurrency(stats?.total_consultations || 0),
      icon: Receipt,
      color: "text-secondary",
      href: "/reports"
    },
    {
      title: "Fatturato Totale",
      value: formatCurrency(stats?.total_revenue || 0),
      icon: TrendingUp,
      color: "text-green-600",
      href: "/reports"
    },
    {
      title: "Scadenze (30gg)",
      value: stats?.expiring_soon || 0,
      icon: AlertTriangle,
      color: "text-destructive",
      href: "/calendar"
    },
    {
      title: "Rate Semestrali (30gg)",
      value: stats?.semiannual_expiring || 0,
      icon: AlertTriangle,
      color: "text-orange-500",
      href: "/calendar"
    },
    {
      title: "Consulenze Mese",
      value: formatCurrency(stats?.monthly_consultations || 0),
      icon: Receipt,
      color: "text-blue-500",
      href: "/reports"
    }
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Panoramica del tuo business assicurativo
        </p>
      </div>

      {/* MEGA Alert */}
      {!driveConnected && !loading && (
        <Card data-testid="drive-alert" className="border-2 border-red-500 bg-red-50 dark:bg-red-950/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CloudOff className="h-6 w-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-700 dark:text-red-400">MEGA non connesso</p>
                  <p className="text-sm text-red-600/70 dark:text-red-400/70">I documenti non verranno sincronizzati su MEGA</p>
                </div>
              </div>
              <span className="text-sm text-red-600/70 dark:text-red-400/70">
                Verifica la sessione MEGA sul server
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="bg-card border-border animate-fadeIn card-hover stat-accent overflow-hidden cursor-pointer group transition-all hover:shadow-md hover:border-primary/40"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => navigate(stat.href)}
            data-testid={`stat-card-${index}`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-1 pt-4 px-5">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color} transition-transform group-hover:scale-110`} strokeWidth={1.8} />
            </CardHeader>
            <CardContent className="pb-4 px-5 flex items-end justify-between">
              <div className="text-2xl font-bold font-heading text-foreground">
                {stat.value}
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground transition-all group-hover:translate-x-0.5" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weekly Expirations - Scadenze Settimana Corrente */}
      {(() => {
        const now = new Date();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Lunedì
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 }); // Domenica
        
        const thisWeekExpiring = expiring.filter(policy => {
          try {
            const expiryDate = parseISO(policy.display_date || policy.expiry_date);
            return isWithinInterval(expiryDate, { start: weekStart, end: weekEnd });
          } catch {
            return false;
          }
        });

        return (
          <Card className="bg-card border-border border-2 border-orange-300 dark:border-orange-700">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-lg">
                  <Calendar className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Scadenze Questa Settimana</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(weekStart)} - {formatDate(weekEnd)}
                  </p>
                </div>
              </div>
              <span className={`text-2xl font-bold ${thisWeekExpiring.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {thisWeekExpiring.length}
              </span>
            </CardHeader>
            <CardContent>
              {thisWeekExpiring.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">Nessuna scadenza questa settimana!</p>
                  <p className="text-sm">Tutte le polizze sono in regola.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {thisWeekExpiring.map((policy, index) => (
                    <div
                      key={`week-${policy.policy_id}-${index}`}
                    onClick={() => navigate(`/policies/${policy.policy_id}`)}
                    className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
                      policy.expiry_type === 'semiannual' 
                        ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800 hover:border-orange-400' 
                        : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 hover:border-red-400'
                    }`}
                    data-testid={`weekly-expiry-${policy.policy_id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        policy.expiry_type === 'semiannual' 
                          ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' 
                          : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                      }`}>
                        {formatDate(policy.display_date || policy.expiry_date)}
                      </span>
                      {policy.renewed && (
                        <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-1.5 py-0.5 rounded">
                          ✓
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-sm truncate">{policy.client_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{policy.company}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs font-mono">{policy.policy_number}</span>
                      <span className="text-xs font-semibold">
                        {formatCurrency(getPolicyGrandTotal(policy))}
                      </span>
                    </div>
                    {/* Quick actions */}
                    <div className="flex gap-1 mt-2 pt-2 border-t border-current/10">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (policy.client_phone) {
                            window.location.href = `tel:${policy.client_phone}`;
                          } else {
                            toast.error("Telefono non disponibile");
                          }
                        }}
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        Chiama
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2 text-xs text-green-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (policy.client_phone) {
                            const phone = policy.client_phone.replace(/\D/g, '');
                            const intlPhone = phone.startsWith('39') ? phone : `39${phone}`;
                            window.open(`https://wa.me/${intlPhone}`, '_blank');
                          } else {
                            toast.error("Telefono non disponibile");
                          }
                        }}
                      >
                        <MessageCircle className="h-3 w-3 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Charts Section */}
      {charts && (
        <div className="space-y-6">
          {/* Row 1: Monthly Trend + Renewal Rate */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Monthly Trend Chart */}
            <Card className="bg-card border-border lg:col-span-2 cursor-pointer hover:shadow-md hover:border-primary/40 transition-all" onClick={() => navigate('/reports')}>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Andamento Ultimi 12 Mesi
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50 ml-auto" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] min-h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <LineChart data={charts.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value, name) => [`€${value.toLocaleString('it-IT')}`, name === 'premi' ? 'Premi' : 'Commissioni']}
                        contentStyle={{ backgroundColor: 'hsl(0, 0%, 100%)', border: '1px solid hsl(40, 10%, 82%)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        labelStyle={{ color: '#71717a' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="premi" name="Premi" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="commissioni" name="Commissioni" stroke={COLORS.success} strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Renewal Rate Card */}
            <Card className="bg-card border-border cursor-pointer hover:shadow-md hover:border-primary/40 transition-all" onClick={() => navigate('/policies')}>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-secondary" />
                  Tasso Rinnovo
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50 ml-auto" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] flex flex-col items-center justify-center">
                  <div className="relative w-40 h-40">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Rinnovate', value: charts.renewal_stats?.renewed || 0 },
                            { name: 'Perse', value: charts.renewal_stats?.not_renewed || 0 },
                            { name: 'In attesa', value: charts.renewal_stats?.pending || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          <Cell fill={COLORS.success} />
                          <Cell fill={COLORS.danger} />
                          <Cell fill={COLORS.muted} />
                        </Pie>
                        <Tooltip 
                          formatter={(value) => [value, 'Polizze']}
                          contentStyle={{ backgroundColor: 'hsl(0, 0%, 100%)', border: '1px solid hsl(40, 10%, 82%)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-foreground">{charts.renewal_stats?.rate || 0}%</div>
                        <div className="text-xs text-muted-foreground">Rinnovate</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 w-full">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.success }}></div>
                        <span>Rinnovate</span>
                      </div>
                      <span className="font-medium">{charts.renewal_stats?.renewed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.danger }}></div>
                        <span>Perse</span>
                      </div>
                      <span className="font-medium">{charts.renewal_stats?.not_renewed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.muted }}></div>
                        <span>In attesa</span>
                      </div>
                      <span className="font-medium">{charts.renewal_stats?.pending || 0}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Policies by Type + Top Companies */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Policies by Type */}
            <Card className="bg-card border-border cursor-pointer hover:shadow-md hover:border-primary/40 transition-all" onClick={() => navigate('/policies')}>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  Polizze per Tipo
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50 ml-auto" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <PieChart>
                      <Pie
                        data={charts.policies_by_type}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {charts.policies_by_type?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name, props) => [
                          `${value} polizze (€${props.payload.premium?.toLocaleString('it-IT') || 0})`,
                          props.payload.name
                        ]}
                        contentStyle={{ backgroundColor: 'hsl(0, 0%, 100%)', border: '1px solid hsl(40, 10%, 82%)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Companies */}
            <Card className="bg-card border-border cursor-pointer hover:shadow-md hover:border-primary/40 transition-all" onClick={() => navigate('/policies')}>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  Top 5 Compagnie
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50 ml-auto" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <BarChart data={charts.top_companies} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis type="number" stroke="#6b7280" fontSize={12} />
                      <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={11} width={100} />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'polizze' ? `${value} polizze` : `€${value.toLocaleString('it-IT')}`,
                          name === 'polizze' ? 'Polizze' : 'Premi'
                        ]}
                        contentStyle={{ backgroundColor: 'hsl(0, 0%, 100%)', border: '1px solid hsl(40, 10%, 82%)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      />
                      <Bar dataKey="polizze" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Expiring by Week */}
          <Card className="bg-card border-border cursor-pointer hover:shadow-md hover:border-primary/40 transition-all" onClick={() => navigate('/calendar')}>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                Scadenze Prossime 4 Settimane
                <ArrowRight className="h-4 w-4 text-muted-foreground/50 ml-auto" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px] min-h-[200px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={charts.expiring_by_week}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="week" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="annuali" name="Scadenze Annuali" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="semestrali" name="Rate Semestrali" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expiring Policies */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-heading">Scadenze Imminenti</CardTitle>
            <p className="text-sm text-muted-foreground">Polizze e rate semestrali - prossimi 30 giorni</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/calendar')}
            data-testid="view-calendar-btn"
          >
            Vedi Calendario
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {expiring.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nessuna scadenza nei prossimi 30 giorni
            </p>
          ) : (
            <div className="space-y-4">
              {expiring.slice(0, 8).map((policy, index) => (
                <div
                  key={`${policy.policy_id}-${policy.expiry_type || 'annual'}-${index}`}
                  onClick={() => navigate(`/policies/${policy.policy_id}`)}
                  className={`flex items-center justify-between p-4 rounded-sm border cursor-pointer hover:shadow-md transition-all ${
                    policy.expiry_type === 'semiannual' 
                      ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 hover:border-orange-400' 
                      : 'bg-muted/50 border-border hover:border-primary'
                  }`}
                  data-testid={`expiring-policy-${policy.policy_id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">
                        {policy.policy_number}
                      </span>
                      <span className="text-xs px-2 py-1 bg-secondary/20 text-secondary-foreground rounded-sm">
                        {policy.policy_type}
                      </span>
                      {policy.expiry_type === 'semiannual' ? (
                        <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded-sm">
                          Rata Semestrale
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 rounded-sm">
                          Scadenza Annuale
                        </span>
                      )}
                      {policy.renewed && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-sm">
                          Rinnovata
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {policy.client_name} • {policy.company}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${policy.expiry_type === 'semiannual' ? 'text-orange-600' : 'text-destructive'}`}>
                      {formatDate(policy.display_date || policy.expiry_date)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(getPolicyGrandTotal(policy))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="bg-card border-border cursor-pointer card-hover group"
          onClick={() => navigate('/clients')}
          data-testid="quick-action-clients"
        >
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-2.5 bg-primary/10 rounded-lg transition-colors duration-150 group-hover:bg-primary/20">
              <Users className="h-5 w-5 text-primary" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Gestisci Clienti</h3>
              <p className="text-xs text-muted-foreground">Aggiungi o modifica clienti</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-card border-border cursor-pointer card-hover group"
          onClick={() => navigate('/policies')}
          data-testid="quick-action-policies"
        >
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-2.5 bg-secondary/20 rounded-lg transition-colors duration-150 group-hover:bg-secondary/30">
              <FileText className="h-5 w-5 text-secondary" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Gestisci Polizze</h3>
              <p className="text-xs text-muted-foreground">Aggiungi o modifica polizze</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-card border-border cursor-pointer card-hover group"
          onClick={() => navigate('/assistant')}
          data-testid="quick-action-assistant"
        >
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-2.5 bg-accent rounded-lg transition-colors duration-150 group-hover:bg-accent/80">
              <AlertTriangle className="h-5 w-5 text-accent-foreground" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Assistente AI</h3>
              <p className="text-xs text-muted-foreground">Chiedi aiuto all'assistente</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
